import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent } from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { RotateCcw, Send, X } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { cn } from "@/lib/utils"
import { AssistantMessage } from "./AssistantMessage"
import { CrisisBlock } from "./CrisisBlock"
import type { AssistantMessage as Message, AssistantStatus } from "./useAssistant"

// The chat panel: a non-modal dialog (the page stays usable behind it),
// bottom sheet on phones and a fixed card on wider screens. Focus lands in
// the composer on open, Escape closes, the transcript is a polite live
// region so screen readers hear replies as they stream.

export type AssistantStrings = {
  title: string
  intro: string
  placeholder: string
  send: string
  close: string
  clear: string
  thinking: string
  privacy: string
  suggestions: string[]
  crisis: { heading: string; body: string; lines: string[] }
}

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  strings: AssistantStrings
  messages: Message[]
  status: AssistantStatus
  crisis: boolean
  onSend: (text: string) => void
  onReset: () => void
  createHandoff: () => Promise<string | null>
}

export function AssistantPanel({
  open,
  onOpenChange,
  strings,
  messages,
  status,
  crisis,
  onSend,
  onReset,
  createHandoff,
}: Props) {
  const [draft, setDraft] = useState("")
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const transcriptRef = useRef<HTMLDivElement>(null)
  const streaming = status === "streaming"

  useEffect(() => {
    const node = transcriptRef.current
    if (node) node.scrollTop = node.scrollHeight
  }, [messages, crisis])

  const submit = (event?: FormEvent) => {
    event?.preventDefault()
    if (streaming || draft.trim().length === 0) return
    onSend(draft)
    setDraft("")
    inputRef.current?.focus()
  }

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault()
      submit()
    }
  }

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange} modal={false}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Content
          aria-describedby={undefined}
          data-testid="assistant-panel"
          onInteractOutside={(event) => event.preventDefault()}
          onOpenAutoFocus={(event) => {
            event.preventDefault()
            inputRef.current?.focus()
          }}
          className={cn(
            "fixed z-50 flex flex-col bg-page text-ink-body shadow-lg outline-none",
            "inset-x-0 bottom-0 h-[85dvh] rounded-t-xl border-t border-hairline",
            "sm:inset-x-auto sm:bottom-4 sm:right-4 sm:h-[600px] sm:max-h-[calc(100dvh-2rem)] sm:w-[380px] sm:rounded-xl sm:border",
          )}
        >
          <header className="flex items-center gap-2 border-b border-hairline px-3 py-2">
            <DialogPrimitive.Title asChild>
              <h2 className="flex-1">{strings.title}</h2>
            </DialogPrimitive.Title>
            {messages.length > 0 && (
              <Button type="button" variant="ghost" size="icon" onClick={onReset} title={strings.clear}>
                <RotateCcw className="h-4 w-4" aria-hidden="true" />
                <span className="sr-only">{strings.clear}</span>
              </Button>
            )}
            <DialogPrimitive.Close asChild>
              <Button type="button" variant="ghost" size="icon" title={strings.close}>
                <X className="h-4 w-4" aria-hidden="true" />
                <span className="sr-only">{strings.close}</span>
              </Button>
            </DialogPrimitive.Close>
          </header>

          <div
            ref={transcriptRef}
            className="flex flex-1 flex-col gap-3 overflow-y-auto px-3 py-3"
            aria-live="polite"
            aria-busy={streaming}
            data-testid="assistant-transcript"
          >
            {messages.length === 0 && (
              <div className="flex flex-col gap-3">
                <p>{strings.intro}</p>
                <ul className="flex flex-col gap-2">
                  {strings.suggestions.map((suggestion) => (
                    <li key={suggestion}>
                      <button
                        type="button"
                        className="w-full cursor-pointer rounded-lg border border-hairline bg-surface px-3 py-2 text-left text-ink-body hover:bg-hairline/40"
                        onClick={() => onSend(suggestion)}
                      >
                        {suggestion}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {messages.map((message, index) =>
              message.role === "user" ? (
                <div key={index} className="flex justify-end">
                  <p className="max-w-[85%] whitespace-pre-wrap rounded-2xl rounded-br-sm bg-accent px-3 py-2 text-page">
                    {message.content}
                  </p>
                </div>
              ) : (
                <div key={index} className="flex justify-start">
                  <div className="max-w-[90%] rounded-2xl rounded-bl-sm border border-hairline bg-surface px-3 py-2">
                    {message.content.length === 0 && streaming ? (
                      <p><em>{strings.thinking}</em></p>
                    ) : (
                      <AssistantMessage
                        content={message.content}
                        createHandoff={createHandoff}
                        onNavigate={() => onOpenChange(false)}
                      />
                    )}
                  </div>
                </div>
              ),
            )}
          </div>

          {crisis && <CrisisBlock {...strings.crisis} />}

          <form onSubmit={submit} className="border-t border-hairline px-3 py-2">
            <label htmlFor="assistant-input" className="sr-only">
              {strings.placeholder}
            </label>
            <div className="flex items-end gap-2">
              <Textarea
                id="assistant-input"
                ref={inputRef}
                rows={2}
                maxLength={4000}
                value={draft}
                placeholder={strings.placeholder}
                onChange={(event) => setDraft(event.target.value)}
                onKeyDown={onKeyDown}
                className="min-h-0 flex-1 resize-none"
              />
              <Button type="submit" size="icon" disabled={streaming || draft.trim().length === 0} title={strings.send}>
                <Send className="h-4 w-4" aria-hidden="true" />
                <span className="sr-only">{strings.send}</span>
              </Button>
            </div>
            <p className="mt-1"><small>{strings.privacy}</small></p>
          </form>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}
